/**
 * C-Search → Live searches. Who is subscribed to live load updates right now, in either system:
 *
 *   - NEW: the ADR-040.2 registry in the search store. It deletes a subscription the minute it expires, so every row
 *     here is active.
 *   - LEGACY: loads-ingest's live search, which clients use today. It keeps ended subscriptions for 24 h, so a status
 *     filter is meaningful there.
 *
 * Side by side, the two are the rollout: what still subscribes on legacy versus what has moved.
 *
 * SAFE BY CONSTRUCTION. Read-only — nothing here can change a subscription. User ids arrive masked by the service
 * that owns them. Pages are keyset-paged on the server (at most 100 rows), so this page holds one page and a stack of
 * the cursors that led to it; "previous" walks the stack back rather than asking for an offset.
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import { LIVE_SEARCH_AUDIT_LIMITS, LIVE_SEARCH_SURFACES, type LiveSearchAuditRow, type LiveSearchAuditSystem } from '@numeo/types-core'
import { loadPipelineApi } from 'api/loadPipeline'
import * as React from 'react'
import { ErrorState, Loading, Stat, StatRow, formatNumber, useLoadPipelineData } from './components/shared'
import { SEARCH_UNREACHABLE } from './SearchProbePage'

const LEGACY_UNREACHABLE = {
    title: 'Legacy live search unreachable',
    body: 'main-service could not reach loads-ingest-service. That says nothing about whether live search is delivering.',
}

const STATUS_COLOR = { active: 'success', cancelled: 'neutral', expired: 'warning' } as const

type Criteria = Record<string, unknown>
const asRecord = (v: unknown): Criteria | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Criteria) : null)
const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])

/** One place, in either system's criteria shape: `{point, radiusMiles}` / `{states}` (new) or `{city, state, latitude, radiusMiles, states}` (legacy). */
function placeText(raw: unknown): string {
    const p = asRecord(raw)
    if (!p) return 'anywhere'
    const states = asList(p.states)
    if (states.length) return states.join(', ')
    const radius = typeof p.radiusMiles === 'number' ? ` +${p.radiusMiles}mi` : ''
    const city = [p.city, p.state].filter((x) => typeof x === 'string' && x).join(', ')
    if (city) return `${city}${radius}`
    const point = asRecord(p.point)
    const lat = point?.lat ?? p.latitude
    const lon = point?.lon ?? p.longitude
    return typeof lat === 'number' && typeof lon === 'number' ? `${lat.toFixed(2)}, ${lon.toFixed(2)}${radius}` : 'anywhere'
}

function criteriaText(c: Criteria): { lane: string; equipment: string; pickup: string } {
    const pickup = asRecord(c.pickup)
    const from = (pickup?.from ?? c.earliestPickup ?? (typeof c.pickupWindowFromIso === 'string' ? c.pickupWindowFromIso.slice(0, 10) : undefined)) as
        | string
        | undefined
    const to = (pickup?.to ?? c.latestPickup ?? (typeof c.pickupWindowToIso === 'string' ? c.pickupWindowToIso.slice(0, 10) : undefined)) as string | undefined
    const equipment = [...asList(c.equipment), ...asList(c.unifiedEquipmentTypes), ...asList(c.equipmentTypes)]
    return {
        lane: `${placeText(c.origin)} → ${placeText(c.destination)}`,
        equipment: equipment.length ? Array.from(new Set(equipment)).join(', ') : 'any',
        pickup: from || to ? `${from ?? '…'} – ${to ?? '…'}` : 'any',
    }
}

/** Minutes until (positive) or since (negative) an instant, phrased the way an operator reads expiry. */
function relative(iso: string): string {
    const minutes = Math.round((Date.parse(iso) - Date.now()) / 60_000)
    const abs = Math.abs(minutes)
    const text = abs < 60 ? `${abs}m` : abs < 48 * 60 ? `${Math.round(abs / 60)}h` : `${Math.round(abs / 1440)}d`
    return minutes >= 0 ? `in ${text}` : `${text} ago`
}

interface Filters {
    projectName: string
    surface: string
    status: string
    limit: number
}

export default function LoadPipelineSearchLivePage() {
    const [system, setSystem] = React.useState<LiveSearchAuditSystem>('legacy')
    const [draft, setDraft] = React.useState<Filters>({ projectName: '', surface: '', status: '', limit: LIVE_SEARCH_AUDIT_LIMITS.defaultPageSize })
    const [filters, setFilters] = React.useState<Filters>(draft)
    /** Cursors of the pages before this one; the last entry is this page's own cursor (`undefined` for the first). */
    const [cursors, setCursors] = React.useState<(string | undefined)[]>([undefined])
    const [selected, setSelected] = React.useState<LiveSearchAuditRow | null>(null)
    const cursor = cursors[cursors.length - 1]

    const { data, error, loading } = useLoadPipelineData(
        () =>
            loadPipelineApi.getLiveSearches(system, {
                projectName: filters.projectName.trim() || undefined,
                surface: filters.surface || undefined,
                status: filters.status || undefined,
                limit: filters.limit,
                cursor,
            }),
        [system, filters, cursor]
    )

    const restart = (next: Partial<{ system: LiveSearchAuditSystem; filters: Filters }>) => {
        if (next.system) setSystem(next.system)
        if (next.filters) setFilters(next.filters)
        setCursors([undefined])
        setSelected(null)
    }

    const activeTotal = Object.values(data?.activeBySurface ?? {}).reduce((a, b) => a + b, 0)

    return (
        <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <FormControl size="sm">
                        <FormLabel>System</FormLabel>
                        <Select size="sm" value={system} onChange={(_, v) => v && restart({ system: v })} sx={{ minWidth: 200 }}>
                            <Option value="legacy">Legacy (loads-ingest)</Option>
                            <Option value="new">New (search registry)</Option>
                        </Select>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Project</FormLabel>
                        <Input
                            size="sm"
                            placeholder="all"
                            value={draft.projectName}
                            onChange={(e) => setDraft({ ...draft, projectName: e.target.value })}
                            sx={{ width: 160 }}
                        />
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Surface</FormLabel>
                        <Select size="sm" value={draft.surface} onChange={(_, v) => setDraft({ ...draft, surface: v ?? '' })} sx={{ minWidth: 170 }}>
                            <Option value="">All</Option>
                            {LIVE_SEARCH_SURFACES.map((s) => (
                                <Option key={s} value={s}>
                                    {s}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Status</FormLabel>
                        <Select size="sm" value={draft.status} onChange={(_, v) => setDraft({ ...draft, status: v ?? '' })} sx={{ minWidth: 130 }}>
                            <Option value="">All</Option>
                            <Option value="active">Active</Option>
                            <Option value="cancelled">Cancelled</Option>
                            <Option value="expired">Expired</Option>
                        </Select>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Per page</FormLabel>
                        <Select size="sm" value={draft.limit} onChange={(_, v) => v && setDraft({ ...draft, limit: v })} sx={{ minWidth: 90 }}>
                            {[25, 50, LIVE_SEARCH_AUDIT_LIMITS.pageSize].map((n) => (
                                <Option key={n} value={n}>
                                    {n}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <Button size="sm" onClick={() => restart({ filters: draft })}>
                        Apply
                    </Button>
                </Box>
                <Typography level="body-xs" textColor="text.tertiary">
                    {system === 'new'
                        ? 'The registry deletes a subscription the minute it expires: every row is active.'
                        : `Legacy keeps ended subscriptions for ${LIVE_SEARCH_AUDIT_LIMITS.windowHours} h. User ids are masked by the service that owns them.`}
                </Typography>
            </Card>

            {error ? <ErrorState error={error} unreachable={system === 'new' ? SEARCH_UNREACHABLE : LEGACY_UNREACHABLE} /> : null}
            {loading && !data ? <Loading /> : null}

            {data ? (
                <>
                    <StatRow>
                        <Stat
                            label="Active subscriptions"
                            value={formatNumber(activeTotal)}
                            hint={filters.projectName ? `project ${filters.projectName}` : 'all projects'}
                        />
                        {Object.entries(data.activeBySurface)
                            .sort((a, b) => b[1] - a[1])
                            .map(([surface, n]) => (
                                <Stat key={surface} label={surface} value={formatNumber(n)} hint="active" />
                            ))}
                    </StatRow>

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 620 }}>
                        <Table size="sm" hoverRow stickyHeader>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Project</th>
                                    <th>User</th>
                                    <th>Surface</th>
                                    <th>Lane</th>
                                    <th>Equipment</th>
                                    <th>Pickup</th>
                                    <th style={{ textAlign: 'right' }}>{system === 'new' ? 'Search since' : 'Created'}</th>
                                    <th style={{ textAlign: 'right' }}>Expires</th>
                                    <th style={{ textAlign: 'right' }}>{system === 'new' ? 'Channel' : 'Loads pushed'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((row) => {
                                    const c = criteriaText(row.criteria)
                                    return (
                                        <tr key={row.subscriptionId} onClick={() => setSelected(row)} style={{ cursor: 'pointer' }}>
                                            <td>
                                                <Chip size="sm" variant="soft" color={STATUS_COLOR[row.status]}>
                                                    {row.status}
                                                </Chip>
                                            </td>
                                            <td>{row.projectName}</td>
                                            <td>
                                                <code>{row.userId}</code>
                                            </td>
                                            <td>{row.surface}</td>
                                            <td>{c.lane}</td>
                                            <td>{c.equipment}</td>
                                            <td>{c.pickup}</td>
                                            <td style={{ textAlign: 'right' }} title={row.createdAt ?? ''}>
                                                {row.createdAt ? relative(row.createdAt) : '—'}
                                            </td>
                                            <td style={{ textAlign: 'right' }} title={row.expiresAt}>
                                                {relative(row.expiresAt)}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{system === 'new' ? row.channel : formatNumber(row.loadsDelivered ?? 0)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                        {data.items.length === 0 ? (
                            <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>
                                No subscriptions match.
                            </Typography>
                        ) : null}
                    </Sheet>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                        <Button size="sm" variant="outlined" disabled={cursors.length <= 1 || loading} onClick={() => setCursors((c) => c.slice(0, -1))}>
                            Previous
                        </Button>
                        <Button size="sm" variant="outlined" disabled={!data.nextCursor || loading} onClick={() => setCursors((c) => [...c, data.nextCursor])}>
                            Next
                        </Button>
                        <Typography level="body-xs" textColor="text.tertiary">
                            Page {cursors.length} · {formatNumber(data.items.length)} row(s), newest expiry first
                        </Typography>
                    </Box>

                    {selected ? (
                        <Card variant="soft" sx={{ mt: 2 }}>
                            <Typography level="title-sm">
                                Subscription <code>{selected.subscriptionId}</code> · search <code>{selected.searchId}</code>
                            </Typography>
                            <Box component="pre" sx={{ m: 0, fontSize: 12, maxHeight: 360, overflow: 'auto' }}>
                                {JSON.stringify(selected.criteria, null, 2)}
                            </Box>
                        </Card>
                    ) : null}
                </>
            ) : null}
        </Box>
    )
}
